/**
 * Secure localStorage utility with error handling and validation
 * Follows security best practices for client-side storage
 */

const STORAGE_PREFIX = "npc_";
const HIGH_SCHOOL_KEY = `${STORAGE_PREFIX}high_school`;

interface StorageValue {
  data: unknown;
  timestamp: number;
  version: string;
}

/**
 * Safely parse JSON with error handling
 */
const safeJSONParse = <T>(value: string, fallback: T): T => {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

/**
 * Check if localStorage is available
 */
const isStorageAvailable = (): boolean => {
  try {
    const test = "__storage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Set item in localStorage with error handling
 */
export const setStorageItem = <T>(key: string, value: T): boolean => {
  if (!isStorageAvailable()) {
    console.warn("localStorage is not available");
    return false;
  }

  try {
    const storageValue: StorageValue = {
      data: value,
      timestamp: Date.now(),
      version: "1.0",
    };
    localStorage.setItem(key, JSON.stringify(storageValue));
    return true;
  } catch (error) {
    console.error("Error setting localStorage item:", error);
    return false;
  }
};

/**
 * Get item from localStorage with error handling
 */
export const getStorageItem = <T>(key: string, fallback: T): T => {
  if (!isStorageAvailable()) {
    return fallback;
  }

  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return fallback;
    }

    const parsed = safeJSONParse<StorageValue>(item, {
      data: fallback,
      timestamp: 0,
      version: "1.0",
    });

    return parsed.data as T;
  } catch (error) {
    console.error("Error getting localStorage item:", error);
    return fallback;
  }
};

/**
 * Remove item from localStorage
 */
export const removeStorageItem = (key: string): boolean => {
  if (!isStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error("Error removing localStorage item:", error);
    return false;
  }
};

/**
 * Clear all NPC-related items from localStorage
 */
export const clearNPCStorage = (): boolean => {
  if (!isStorageAvailable()) {
    return false;
  }

  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith(STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
    return true;
  } catch (error) {
    console.error("Error clearing NPC storage:", error);
    return false;
  }
};

// High School specific storage functions
export interface HighSchoolData {
  ceeb: number;
  hsName: string;
}

export const setHighSchoolData = (data: HighSchoolData): boolean => {
  return setStorageItem(HIGH_SCHOOL_KEY, data);
};

export const getHighSchoolData = (): HighSchoolData | null => {
  return getStorageItem<HighSchoolData | null>(HIGH_SCHOOL_KEY, null);
};

export const clearHighSchoolData = (): boolean => {
  return removeStorageItem(HIGH_SCHOOL_KEY);
};
